import { useNavigate } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { IntentName } from "@/design";
import { useCan } from "@/shared/auth";
import { cn } from "@/shared/lib/cn";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";

import { useMarkRead } from "../api/use-notification-mutations";
import {
  SEVERITY_INTENT,
  formatRelativeTime,
  targetToNavigation,
  targetToRoute,
} from "../lib/notif";
import type { NotificationEvent } from "../lib/notif";

interface NotificationCardProps {
  event: NotificationEvent;
  variant: "compact" | "expanded";
  /** Called right before navigating away (compact rows use it to close the drawer). */
  onNavigate?: () => void;
}

/** Strong intent color used for the severity rail + unread tint. */
function accentVar(intent: IntentName): string {
  return `var(--intent-${intent}-fg)`;
}

export function NotificationCard({
  event,
  variant,
  onNavigate,
}: NotificationCardProps): JSX.Element {
  const { t } = useTranslation("notifications");
  const markRead = useMarkRead();
  // Mark-read is a write (spec §6.6): read-only users can still open the
  // notification, it just stays unread.
  const canWrite = useCan("notifications:write");
  const navigate = useNavigate();

  const intent = SEVERITY_INTENT[event.severity];
  const accent = accentVar(intent);
  const ctaRoute = targetToRoute(event.target);
  const navOptions = targetToNavigation(event.target);
  const entityMetric = [event.entity, event.metric].filter(Boolean).join(" · ");

  function goToTarget(): void {
    if (navOptions === null) return;
    onNavigate?.();
    void navigate(navOptions);
  }

  function handleCtaClick(e: React.MouseEvent<HTMLAnchorElement>): void {
    if (navOptions === null) return;
    e.preventDefault();
    goToTarget();
  }

  // Severity rail: full-strength for unread, dimmed for read. Unread rows also
  // get a whisper-thin intent tint so the feed scans read-vs-unread instantly.
  const rail = (
    <span
      aria-hidden
      className="absolute inset-y-0 left-0 w-[3px]"
      style={{ background: accent, opacity: event.read ? 0.3 : 1 }}
    />
  );
  const severityBadge = (
    <Badge intent={intent} dot data-testid={`notif-severity-${event.id}`}>
      {t(`severity.${event.severity}`)}
    </Badge>
  );
  const time = (
    <time className="shrink-0 text-[11px] font-medium text-[var(--text-subtle)]">
      {formatRelativeTime(event.created_at)}
    </time>
  );
  const title = (
    <p
      className={cn(
        "text-sm leading-snug",
        event.read
          ? "font-medium text-[var(--text-secondary)]"
          : "font-semibold text-[var(--text-primary)]",
      )}
    >
      {event.title}
    </p>
  );

  // ---- Compact (bell drawer): the whole row is one clean clickable target. ----
  if (variant === "compact") {
    return (
      <button
        type="button"
        data-testid={`notif-row-${event.id}`}
        onClick={() => {
          if (canWrite && !event.read) markRead.mutate(event.id);
          goToTarget();
        }}
        className={cn(
          "group relative flex w-full flex-col gap-1 border-b border-[var(--premium-border)] py-3 pl-5 pr-4 text-left transition-colors focus-ring",
          "hover:bg-[var(--surface-sunken)]",
        )}
        style={
          event.read ? undefined : { background: `color-mix(in srgb, ${accent} 5%, transparent)` }
        }
      >
        {rail}
        <div className="flex items-center justify-between gap-2">
          {severityBadge}
          {time}
        </div>
        <div className="truncate">{title}</div>
        {entityMetric && (
          <p className="truncate text-xs text-[var(--text-subtle)]">{entityMetric}</p>
        )}
        {ctaRoute !== null && (
          <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-[var(--brand-600)] opacity-0 transition-opacity duration-150 group-hover:opacity-100">
            {t("action.view_in_report")}
            <ArrowRight aria-hidden className="h-3 w-3" />
          </span>
        )}
        {!event.read && <span className="sr-only">{t("state.unread")}</span>}
      </button>
    );
  }

  // ---- Expanded (inbox page): full detail + per-row actions. ----
  return (
    <div
      data-testid={`notif-row-${event.id}`}
      className={cn(
        "relative flex flex-col gap-1.5 border-b border-[var(--premium-border)] py-4 pl-5 pr-4 transition-colors",
      )}
      style={
        event.read ? undefined : { background: `color-mix(in srgb, ${accent} 4%, transparent)` }
      }
    >
      {rail}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {severityBadge}
          {!event.read && <span className="sr-only">{t("state.unread")}</span>}
        </div>
        {time}
      </div>
      {title}
      {entityMetric && <p className="text-xs text-[var(--text-subtle)]">{entityMetric}</p>}
      <p className="text-xs leading-relaxed text-[var(--text-secondary)]">{event.message}</p>

      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        {canWrite && !event.read && (
          <Button
            variant="outline"
            size="sm"
            data-testid={`notif-markread-${event.id}`}
            onClick={() => markRead.mutate(event.id)}
            disabled={markRead.isPending}
          >
            {t("action.mark_read")}
          </Button>
        )}
        {ctaRoute !== null && (
          <a
            href={ctaRoute}
            data-testid={`notif-cta-${event.id}`}
            onClick={handleCtaClick}
            className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-[var(--brand-600)] transition-colors hover:text-[var(--brand-700)] focus-ring"
          >
            {t("action.view_in_report")}
            <ArrowRight aria-hidden className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  );
}
